import { useEffect, useMemo, useState } from "react";
import { Feature } from "geojson";
import { isEqual } from "lodash";

import { layersSlice, selectLayerByVersionAndConfigId, selectLayerConfigByKey } from "fond/api";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

/**
 * Helper function to clone features and add layerId property.
 * This allows features from different layers to be distinguished during save.
 */
const addLayerIdToFeatures = <T extends Feature>(features: T[] | undefined, layerId: string): T[] => {
  if (!features) return [];
  return structuredClone(features).map((f) => ({
    ...f,
    properties: { ...f.properties, layerId },
  }));
};

interface LayerFeaturesResult {
  layerKey: string;
  features: Feature[];
  isLoading: boolean;
  isError: boolean;
}

interface UseMultiLayerFeaturesOptions {
  /**
   * Optional mapping to override the layerId property on features.
   * If a layerKey has a mapping, features from that layer will have their
   * properties.layerId set to the mapped value instead of the original layerKey.
   * Layers not in the mapping will keep their original layerKey as the layerId.
   *
   * @example
   * {
   *   layerIdMapping: {
   *     "solve_hubs_t1": "hubs",
   *     "solve_hubs_t2": "hubs",
   *     // ugPoints not mapped, so keeps "ugPoints" as its layerId
   *   }
   * }
   */
  layerIdMapping?: Record<string, string>;
}

/**
 * Custom hook to fetch features for multiple layers.
 *
 * This hook fetches feature data for all specified layer keys and combines them
 * into a single array. Each feature is tagged with its layerId for later identification.
 *
 * Uses RTK Query's dispatch-based fetching to avoid React hooks limitations.
 *
 * @param versionId - The version ID to fetch layers from
 * @param layerKeys - Array of layer keys (e.g., ["demand", "leadins"])
 * @param options - Optional configuration for layer ID mapping
 * @returns Object containing combined features, loading state, and error state
 *
 * @example
 * // Basic usage - features get properties.layerId from their source layer
 * const { features, isLoading } = useMultiLayerFeatures(versionId, ["demand", "leadins"]);
 *
 * @example
 * // Map specific layers to different layer IDs
 * const { features, isLoading } = useMultiLayerFeatures(
 *   versionId,
 *   ["solve_hubs_t1", "solve_hubs_t2", "ugPoints"],
 *   {
 *     layerIdMapping: {
 *       "solve_hubs_t1": "hubs",
 *       "solve_hubs_t2": "hubs",
 *       // ugPoints not in mapping, keeps original ID
 *     }
 *   }
 * );
 */
export const useMultiLayerFeatures = (
  versionId: string,
  layerKeys: string[],
  options?: UseMultiLayerFeaturesOptions
): {
  features: Feature[];
  isLoading: boolean;
  isError: boolean;
} => {
  const dispatch = useAppDispatch();
  const [layerResults, setLayerResults] = useState<LayerFeaturesResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get all layer metadata in one selector call with deep equality check
  // Use LayerConfig first (always available), then get Layer if it exists (only when features uploaded)
  const layers = useAppSelector((state) => {
    return layerKeys.map((layerKey) => {
      const layerConfig = selectLayerConfigByKey(state, { versionId, layerKey });
      const layer = layerConfig ? selectLayerByVersionAndConfigId(state, { versionId, configId: layerConfig.ID }) : undefined;

      return {
        layerKey,
        layerId: layer?.ID, // undefined if layer not uploaded = no features to fetch
      };
    });
  }, isEqual);

  // Fetch features for all layers using dispatch
  useEffect(() => {
    // Skip if no layers
    if (layerKeys.length === 0) {
      setLayerResults([]);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    // Track subscriptions for cleanup
    const subscriptions: Array<{ unsubscribe: () => void }> = [];

    // Create promises for all layer fetches
    const fetchPromises = layers.map(async ({ layerKey, layerId }) => {
      if (!layerId) {
        return {
          layerKey,
          features: [],
          isLoading: false,
          isError: false,
        };
      }

      // Use RTK Query's initiate method to fetch data
      const querySubscription = dispatch(layersSlice.endpoints.getFeatures.initiate({ layerId }, { forceRefetch: false }));
      subscriptions.push(querySubscription);

      try {
        const result = await querySubscription.unwrap();

        // Use mapped layer ID if available, otherwise use original layerKey
        const targetLayerId = options?.layerIdMapping?.[layerKey] ?? layerKey;

        return {
          layerKey,
          features: addLayerIdToFeatures(result, targetLayerId),
          isLoading: false,
          isError: false,
        };
      } catch (error) {
        console.error(`Failed to fetch features for layer ${layerKey}:`, error);
        return {
          layerKey,
          features: [],
          isLoading: false,
          isError: true,
        };
      } finally {
        // Unsubscribe after data is received
        querySubscription.unsubscribe();
      }
    });

    // Wait for all fetches to complete
    Promise.all(fetchPromises).then((results) => {
      if (!cancelled) {
        setLayerResults(results);
        setIsLoading(false);
      }
    });

    // Cleanup: unsubscribe from any remaining subscriptions on unmount or re-run
    return () => {
      cancelled = true;
      subscriptions.forEach((sub) => sub.unsubscribe());
    };
  }, [dispatch, layers, layerKeys.length, options?.layerIdMapping]);

  // Combine results
  return useMemo(() => {
    const features: Feature[] = [];
    let isError = false;

    layerResults.forEach((result) => {
      features.push(...result.features);
      if (result.isError) {
        isError = true;
      }
    });

    return {
      features,
      isLoading,
      isError,
    };
  }, [layerResults, isLoading]);
};
